import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getConsent, setConsent, ConsentStatus } from "@/lib/consent";
import { loadAnalytics, unloadAnalytics } from "@/lib/analytics";
import CookieSettings from "./CookieSettings";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('unset');

  useEffect(() => {
    // Check consent on mount
    const status = getConsent();
    setConsentStatus(status);

    // Show banner only if consent hasn't been given yet
    if (status === 'unset') {
      // Small delay for smoother page load
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }

    // If already accepted, load analytics
    if (status === 'accepted') {
      loadAnalytics();
    }
  }, []);

  // Listen for consent changes from settings modal
  useEffect(() => {
    const handleConsentChange = (e: CustomEvent<'accepted' | 'rejected'>) => {
      setConsentStatus(e.detail);
      setShowBanner(false);
      
      if (e.detail === 'accepted') {
        loadAnalytics();
      } else {
        unloadAnalytics();
      }
    };

    window.addEventListener('consentChanged', handleConsentChange as EventListener);
    return () => window.removeEventListener('consentChanged', handleConsentChange as EventListener);
  }, []);

  const handleAccept = () => {
    setConsent('accepted');
    setConsentStatus('accepted');
    setShowBanner(false);
    loadAnalytics();
  };

  const handleReject = () => {
    setConsent('rejected');
    setConsentStatus('rejected');
    setShowBanner(false);
  };

  const handleManage = () => {
    setShowSettings(true);
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          aria-modal="false"
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-50 animate-in slide-in-from-bottom-4 duration-300"
        >
          <div className="bg-card border border-border rounded-lg shadow-lg p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h2 className="text-base font-medium text-foreground">Privacy settings</h2>
              <button
                onClick={handleReject}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
                aria-label="Close and reject analytics"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              We use essential cookies to make this site work. With your consent, we also use minimal analytics to understand how the site is used. We do not sell personal data.
            </p>
            
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={handleAccept}
                size="sm"
                className="bg-foreground text-background hover:bg-foreground/90"
              >
                Accept analytics
              </Button>
              <Button
                onClick={handleReject}
                variant="outline"
                size="sm"
              >
                Reject
              </Button>
              <button
                onClick={handleManage}
                className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors ml-1"
              >
                Manage
              </button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3">
              <Link to="/privacy" className="hover:text-foreground underline underline-offset-2 transition-colors">
                Privacy Policy
              </Link>
              {" · "}
              <Link to="/cookies" className="hover:text-foreground underline underline-offset-2 transition-colors">
                Cookie Details
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <CookieSettings
        open={showSettings}
        onOpenChange={setShowSettings}
        currentStatus={consentStatus}
      />
    </>
  );
};

export default CookieConsent;
